import { createClient } from 'microcms-js-sdk';
import MicroModal from 'micromodal';

/**
 * Class ProductDetails
 */
class ProductDetails {
	transition: number;
	contentId: string;
	loadingElement: JQuery;
	block: JQuery;
	container: JQuery;
	client: ReturnType<typeof createClient>;

	constructor(element: JQuery) {
		this.block = element;
		this.transition = 400;
		this.contentId = '';
		this.loadingElement = this.block.find('#js_loading');
		this.container = this.block.find('.block-product__wrapper');
		// microCMSクライアントの初期化
		this.client = createClient({
			serviceDomain: '3kxo8u9nf1', // microCMSのサービスドメイン
			apiKey: 'c6086e40236a405c9410b8c52bed50b428f0', //画面プレビュー用
			// apiKey: 'ac14fd9441e74441bf3d27c001e7ddb131fb', //本番用
		});

		this.init();
	}

	/**
	 * Initialize
	 */
	init() {
		this.initializeProducts();
	}

	/**
	 * 初期化処理
	 * 初回ロード時にクエリパラメータからcontentIdを取得
	 */
	initializeProducts() {
		const urlParams = new URLSearchParams(window.location.search);
		this.contentId = urlParams.get('contentId') || '';
		if (!this.contentId) {
			console.error('contentIdが見つかりません。');
		} else {
			this.fetchProducts();
		}

		// back to index
		const backToIndexLinks = this.block.find('#js-backto-index');

		// contentIdの値に基づいて遷移先URLを決定
		let targetUrl = '/us/products/search/'; // デフォルト値
		if (
			this.contentId.includes('fibers') ||
			this.contentId.includes('textiles') ||
			this.contentId.includes('apparel')
		) {
			targetUrl = '/us/products/fibers-textiles/';
		} else if (
			this.contentId.includes('resins') ||
			this.contentId.includes('chemicals') ||
			this.contentId.includes('films') ||
			this.contentId.includes('electronics')
		) {
			targetUrl = '/us/products/performance-chemicals/';
		} else if (this.contentId.includes('water')) {
			targetUrl = '/us/products/water-environment/';
		} else if (this.contentId.includes('carbon')) {
			targetUrl = '/us/products/carbon-fiber/';
		} else if (
			this.contentId.includes('medical') ||
			this.contentId.includes('pharmaceuticals')
		) {
			targetUrl = '/us/products/pharmaceuticals-medical/';
		}

		// 遷移先のURLをhref属性に設定
		backToIndexLinks.attr('href', targetUrl);
	}

	/**
	 * Fetch products from the API
	 * @param page
	 */
	async fetchProducts() {
		// loadingを表示
		this.loadingElement.addClass('is_show');

		// APIリクエスト
		try {
			const data = await this.client.get({
				endpoint: 'toray_products',
				contentId: this.contentId,
			});
			this.updateJsonLd(data);
			this.updateProductDetails(data);
		} catch (error) {
			console.error('Error fetching products:', error);
		} finally {
			// loading非表示
			setTimeout(() => {
				this.container.addClass('is_show');
				this.loadingElement.removeClass('is_show');
			}, this.transition);
		}
	}

	/**
	 * Update jsonld with API data
	 * @param data - APIレスポンスデータ
	 */
	updateJsonLd(data: any) {
		// JSON-LDスクリプトタグを取得
		const jsonLdElement = document.querySelector("script[type='application/ld+json']");
		if (!jsonLdElement) {
			console.error('JSON-LDスクリプトが見つかりません');
			return;
		}

		// 現在のJSON-LDをパース
		let jsonLdData;
		try {
			jsonLdData = JSON.parse(jsonLdElement.textContent || '{}');
		} catch (error) {
			console.error('JSON-LDのパースに失敗しました:', error);
			return;
		}

		// 現在のURLを取得
		const currentUrl = window.location.href;

		// JSON-LDデータの変更
		const contentId = data.id || 'unknown-id';
		const productName = data.productNameForList || 'Unknown Product';

		// 動的データを反映
		jsonLdData['@graph'][0]['@id'] = currentUrl;
		jsonLdData['@graph'][0].url = currentUrl;
		jsonLdData['@graph'][0].breadcrumb['@id'] = `${currentUrl}#breadcrumb`;
		jsonLdData['@graph'][0].potentialAction[0].target = [currentUrl];
		jsonLdData['@graph'][1]['@id'] = `${currentUrl}#breadcrumb`;
		jsonLdData['@graph'][1].itemListElement[2].name = productName;

		// JSON-LDデータをスクリプトに反映
		jsonLdElement.textContent = JSON.stringify(jsonLdData, null, 2);
	}

	/**
	 * Update product detail with API data
	 * @param data - APIレスポンスデータ
	 */
	updateProductDetails(data: any) {
		const language =
			(this.block.find('#js_language') as JQuery<HTMLInputElement>)?.val() || 'en'; // 言語設定を取得
		const postedSite =
			(document.getElementById('js_posted_site') as HTMLInputElement)?.value || 'us'; // 掲出先

		// レスポンスデータがある場合はグリッドを作成
		const itemId = data.id;

		// productName
		let productNameObj = data.productName.find(
			(pn: any) =>
				pn.lang?.includes(language) &&
				(pn.site.length === 0 || pn.site.includes(postedSite))
		);
		if (!productNameObj) {
			productNameObj = data.productName.find((pn: any) => pn.lang?.includes(language));
		}
		if (!productNameObj) {
			productNameObj = data.productName.find(
				(pn: any) => pn.lang === 'en' && pn.site.includes('global')
			);
		}
		const productName = productNameObj?.text || '';
		document.querySelector('.block-product__title h1')!.textContent = productName;

		// commonName
		let commonNameObj = data.commonName.find((cn: any) => cn.lang?.includes(language));
		if (!commonNameObj) {
			commonNameObj = data.commonName.find((cn: any) => cn.lang === 'en');
		}
		const commonName = commonNameObj?.text || '';
		const commonNameElement = document.getElementById('js_products_commonName');
		if (commonNameElement) {
			if (commonName) {
				commonNameElement.textContent = commonName;
			} else {
				commonNameElement.style.display = 'none';
			}
		}

		// productImages
		const imagesContainer = document.getElementById('js_images_container');
		const modalsContainer = document.getElementById('js_modal_container');

		if (!imagesContainer || !modalsContainer) {
			console.error('必要なコンテナが見つかりません');
			return;
		}

		// 画像コンテナとモーダルコンテナを初期化
		imagesContainer.innerHTML = '';
		modalsContainer.innerHTML = '';

		if (data.productImages && data.productImages.length > 0) {
			data.productImages.forEach(
				(image: { url: string; height: number; width: number }, index: number) => {
					// 画像表示用の`figure`タグを生成
					const figure = document.createElement('figure');
					figure.classList.add('block-product__image');
					figure.setAttribute('data-micromodal-trigger', `modal-${index}`);

					const img = document.createElement('img');
					img.src = image.url;
					img.alt = `Product Image ${index + 1}`;
					img.width = image.width;
					img.height = image.height;
					img.decoding = 'async';
					img.fetchPriority = 'high';
					img.classList.add('attachment-md', 'size-md');

					figure.appendChild(img);
					imagesContainer.appendChild(figure);

					// モーダル用HTMLを生成
					const modalHTML = `
					<div class="modal micromodal-slide modal--image" id="modal-${index}" aria-hidden="true">
							<div class="modal__overlay" tabindex="-1" data-micromodal-close>
									<div class="modal__container" role="dialog" aria-modal="true">
											<button class="modal__close" data-micromodal-close>
													<span class="sr-only">Close</span>
											</button>
											<main class="modal__content" id="modal-${index}-content">
													<figure>
															<img
																	src="${image.url}"
																	alt="Product Image ${index + 1}"
																	width="${image.width}"
																	height="${image.height}"
																	decoding="async"
																	loading="lazy"
																	class="attachment-full-width size-full-width"
															/>
													</figure>
											</main>
									</div>
							</div>
					</div>
					`;
					modalsContainer.insertAdjacentHTML('beforeend', modalHTML);
				}
			);

			// モーダルライブラリの初期化
			if (typeof MicroModal !== 'undefined') {
				MicroModal.init({
					disableScroll: true, // モーダルが開いたときにスクロールを無効化
					awaitOpenAnimation: true, // 開閉アニメーションを待つ
				});
			} else {
				console.error('MicroModalがロードされていません');
			}
		} else {
			// 画像が存在しない場合
			const noimageUrl =
				'https://images.microcms-assets.io/assets/fa7ebd3c9a484c6197223084f64eb8d7/6bbeebcd64a04eb5b0be2ca01e6daba6/products_noimage.jpg';

			imagesContainer.innerHTML = `
				<figure
					class="block-product__image"
				>
					<img
						fetchpriority="high"
						decoding="async"
						width="640"
						height="400"
						src="${noimageUrl}"
						class="attachment-md size-md"
						alt=""
						sizes="(max-width: 640px) 100vw, 640px"
					/>
				</figure>
			`;
		}

		// productDescription
		// langが合致するeditorを取得。該当がない場合はlang: enを取得
		let productDescriptionObj = data.productDescription.find((pd: any) =>
			pd.lang.includes(language)
		);

		if (!productDescriptionObj) {
			productDescriptionObj = data.productDescription.find((pd: any) =>
				pd.lang.includes('en')
			);
		}

		let productDescription = productDescriptionObj?.editor || 'Description not available.';

		// DOMパーサーを使用してHTMLを解析
		const parser = new DOMParser();
		const doc = parser.parseFromString(productDescription, 'text/html');

		console.log(doc);

		// editorButtonLinkクラスを持つ要素を探す
		const editorButtonLinks = doc.querySelectorAll('a span.editorButtonLink');

		editorButtonLinks.forEach((link) => {
			const parentAnchor = link.closest('a');
			if (parentAnchor) {
				const href = parentAnchor.getAttribute('href');
				const target = parentAnchor.getAttribute('target');
				const text = link.textContent;

				// 新しいボタン要素を作成
				const newButton = doc.createElement('a');
				newButton.className = 'btn btn-primary btn-has-icon has-animated-circle';
				newButton.href = href || '#';
				if (target) {
					newButton.target = target;
				}
				newButton.innerHTML = `
        <span class="animated-circle" style="--circle-width: 32px; --half-circle-width: 16px">
          <span class="animated-circle__side animated-circle__side--right">
            <span class="animated-circle__inner animated-circle__inner--right"></span>
          </span>
          <span class="animated-circle__side animated-circle__side--left">
            <span class="animated-circle__inner animated-circle__inner--left"></span>
          </span>
          <span class="animated-circle__icon">
            <i class="icon-large-arrow-right"></i>
          </span>
        </span>
        ${text}
      `;

				// 元のリンクをボタンで置き換え
				parentAnchor.parentNode?.replaceChild(newButton, parentAnchor);
			}
		});

		// 変換後のHTMLを取得
		productDescription = doc.body.innerHTML;

		const descriptionElement = document.querySelector('.block-product__description');
		if (descriptionElement) {
			descriptionElement.innerHTML = productDescription;
		}

		// descriptionElement!.innerHTML =
		// 	data.productDescription.find((desc: any) => desc.lang.includes(language))?.editor || '';

		// contactURL
		// langが合致するurlを取得
		const contactURLObj =
			data.contactURL.find(
				(cu: any) => cu.lang.includes(language) && cu.site.includes(postedSite)
			) || data.contactURL.find((cu: any) => cu.lang.includes(language));
		const contactURL = contactURLObj ? contactURLObj.url : '';

		const contactURLElement = document.getElementById('js_contact_url');
		if (contactURLElement) {
			const contactParentElement = contactURLElement.parentElement;

			if (contactURL) {
				contactURLElement.setAttribute('href', contactURL);

				// Check if the URL is external
				if (
					contactURL.startsWith('http') &&
					!/^https?:\/\/[^/]+\.toray\./.test(contactURL)
				) {
					contactURLElement.setAttribute('target', '_blank');
				} else {
					contactURLElement.removeAttribute('target');
				}

				if (contactParentElement) {
					contactParentElement.style.display = '';
				}
			} else if (contactParentElement) {
				contactParentElement.style.display = 'none';
			}
		}

		// Update js_contact_cover_url
		const contactCoverURLElement = document.getElementById('js_contact_cover_url');
		if (contactCoverURLElement && contactURL && contactURL.includes('/us/contact/?group=')) {
			contactCoverURLElement.setAttribute('href', contactURL);
		}

		// freeArea
		const freeAreaObj = data.freeArea?.find((fa: any) => fa.lang.includes(language));
		const freeAreaText = freeAreaObj?.text || ''; // デフォルトで空文字

		const freeAreaElement = document.querySelector('.block-product__free') as HTMLElement;
		if (freeAreaElement) {
			if (freeAreaText) {
				// freeAreaが存在する場合は反映
				freeAreaElement.textContent = freeAreaText;
				freeAreaElement.style.display = ''; // 表示
			} else {
				// freeAreaが存在しない場合は非表示
				freeAreaElement.style.display = 'none';
			}
		}

		// metaデータ
		document.title = `${productName} ${commonName} | Products | TORAY United States`;
		document
			.querySelector("meta[name='description']")
			?.setAttribute('content', productDescription[0].editor);
		document
			.getElementById('js_og_title')
			?.setAttribute(
				'content',
				`${productName} ${commonName} | Products | TORAY United States`
			);
		document.getElementById('js_og_url')?.setAttribute('content', window.location.href);
		document.getElementById('js_canonical')?.setAttribute('href', window.location.href);
	}
}

// クラスインスタンスの作成
const productDetails: ProductDetails[] = [];
$('#js_block_products_details').each(function () {
	const productDetailInstance = new ProductDetails($(this));
	productDetails.push(productDetailInstance);
});
